import * as React from 'react';
import * as S from './stat-card.styles';

/**
 * A single headline figure: a label, a value, and a line of context under it.
 *
 * Deliberately not `claim-details/metrics-card` — that one is a chart card bound to a dataset and a
 * date range, and it is scoped to claim metrics. This is the flat KPI tile the denials dashboard
 * puts across the top of the page, and it is the only genuinely new visual primitive that surface
 * needed.
 */
export type StatTone = 'neutral' | 'urgent';

export interface IStatCardProps {
    /** Small uppercase heading — `Open denials`. */
    label: string;
    /**
     * The figure. Pass `undefined` for "not available yet" rather than `0`: a zero is a
     * measurement and would assert something about the practice's money that we cannot support.
     */
    value?: React.ReactNode;
    /** The line under the value — `last 30 days`, `avg $2,821 / claim`. */
    sub?: React.ReactNode;
    /** `urgent` colours the value; use it where the number is a deadline, not a total. */
    tone?: StatTone;
    /** Shown in place of the value when `value` is undefined, and as its title attribute. */
    unavailableReason?: string;
}

export const StatCard: React.FC<IStatCardProps> = ({ label, value, sub, tone = 'neutral', unavailableReason }) => (
    <S.StatCard>
        <S.StatLabel>{label}</S.StatLabel>
        {value === undefined || value === null ? (
            <S.StatUnavailable title={unavailableReason}>&mdash;</S.StatUnavailable>
        ) : (
            <S.StatValue urgent={tone === 'urgent'}>{value}</S.StatValue>
        )}
        {sub ? <S.StatSub>{sub}</S.StatSub> : null}
    </S.StatCard>
);

export const StatCardRow = S.StatCardRow;
